
import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import BlogPostCard from '@/components/blog/BlogPostCard';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

// Function to fetch blog posts
const fetchBlogPosts = async () => {
  try {
    
    const response = await axios.get('/api/blog-posts');
    
    // Log the response to help with debugging
    
    
    // Ensure we return an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return []; // Return empty array on error
  }
};

// Function to fetch blog groups
const fetchBlogGroups = async () => {
  try {
    
    const response = await axios.get('/api/blog-groups');
    
    // Log the response to help with debugging
    
    
    // Ensure we return an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching blog groups:', error);
    return []; // Return empty array on error
  }
};

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('All');
  const [blogGroups, setBlogGroups] = useState([{ id: 'all', name: 'All' }]);

  const { data: blogPosts = [], isLoading, error } = useQuery({
    queryKey: ['blogPosts'],
    queryFn: fetchBlogPosts
  });

  // Log what we get from the API query for debugging
  useEffect(() => {
    
  }, [blogPosts]);

  // Fetch blog groups
  useEffect(() => {
    const getGroups = async () => {
      try {
        const groups = await fetchBlogGroups();
        
        setBlogGroups([
          { id: 'all', name: 'All' },
          ...groups
        ]);
      } catch (error) {
        console.error('Error in getGroups:', error);
      }
    };
    
    getGroups();
  }, []);

  // Make sure blogPosts is an array before mapping
  const safeArray = Array.isArray(blogPosts) ? blogPosts : [];

  // Transform the data format to match BlogPostCard props
  const transformedPosts = safeArray.map(post => {
    // Extract tags from the nested structure if available
    const tags = post.blog_tags
      ? post.blog_tags.map(tag => tag.tags?.name).filter(Boolean)
      : [];
    
    // Use slug from backend, fallback to transforming title
    const slug = post.slug
      ? post.slug
      : post.title.trim().replace(/\s+/g, '-');
    
    // Format the date if it exists
    const date = post.created_at 
      ? new Date(post.created_at).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      : '';

    return {
      id: post.id || '',
      title: post.title || '',
      excerpt: post.excerpt || '',
      author: post.author || '',
      date: date,
      readTime: post.read_time || '5 min read',
      slug: slug,
      tags: tags,
      groupId: post.group_id || '',
      groupName: post.blog_groups?.name || post.group_name || ''
    };
  });

  // Filter blog posts based on search and group
  const filteredPosts = transformedPosts.filter(post => {
    const matchesSearch = 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesGroup = selectedGroup === 'All' || 
      post.groupId === selectedGroup;
    
    return matchesSearch && matchesGroup;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                Blog
              </h1>
              <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Insights, tutorials, and stories from the Pixel Tools team
              </p>
              
              {/* Search Bar */}
              <div className="max-w-md mx-auto mt-6 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Blog Groups */}
        <section className="py-8 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex overflow-x-auto pb-2 space-x-4 no-scrollbar">
              {blogGroups.map((group) => (
                <button 
                  key={group.id}
                  onClick={() => setSelectedGroup(group.id === 'all' ? 'All' : group.id)}
                  className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full transition-colors
                    ${selectedGroup === (group.id === 'all' ? 'All' : group.id)
                      ? 'bg-primary text-white' 
                      : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                    }`}
                >
                  {group.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold">
                {selectedGroup === 'All' ? 'All Articles' : blogGroups.find(g => g.id === selectedGroup)?.name + ' Articles'}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {isLoading ? (
                <div className="col-span-full text-center py-12">
                  <p>Loading blog posts...</p>
                </div>
              ) : filteredPosts.length > 0 ? (
                filteredPosts.map((post) => (
                  <BlogPostCard key={post.id} post={post} />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-gray-500 text-lg">No articles match your search criteria.</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Newsletter Subscription */}
        <section className="py-16 bg-gray-50 dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Subscribe to our newsletter
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Get the latest articles, tutorials, and updates from our team delivered straight to your inbox.
            </p>
            <div className="max-w-md mx-auto flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-grow px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button className="button-primary whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Blog;
